(ns ui.enrich.apply
  "Apply a completed enrichment result onto a just-created transaction process.

   The create modal only persists the few fields it shows (name, vehicle,
   template, transaction type). This namespace fills in everything else the AI
   extracted: the root preparation fields on the transaction process, and the
   diligence-declaration slot entities. Values arrive loosely typed from the
   model (e.g. \"11.8%\", \"GBP 60,000,000\", \"coi-answer/no\", \"GB\",
   \"2026-12-15\") and are cleaned to the stored type here, because the backend's
   own coercion only accepts already-clean inputs (numbers without currency /
   separators, enum values as keywords or exact labels, dates as epoch-ms).

   Pure transforms (clean-value, *-updates, *-creates) are isolated from the
   effecting `::run` fx so they can be unit-tested directly."
  (:require
   [clojure.string :as str]
   [common.currencies :as currencies]
   [common.localization :as loc]
   [common.schema.registry :as schema-registry]
   [common.transaction-process.resolver :as resolver]
   [common.transaction-process.templates.registry :as registry]
   [common.util :as cu]
   [frontend.actions.declaration :as declaration-actions]
   [frontend.actions.entity :as entity-actions]
   [frontend.conn :as conn]
   [frontend.logging :as logging]
   [frontend.routing :as routing]
   [frontend.updates.entity-scope :as entity-scope]
   [frontend.utils :as fu]
   [fx.ajax :as ajax]
   [promesa.core :as p]
   [re-frame.core :as rf]
   [ui.components.toast.view :as toast]
   [ui.enrich.event :as enrich-event]
   [ui.enrich.model :as enrich-model]))

(def drop-marker ::drop)

;; Fields the create modal already persisted on the new process; never re-applied.
(def ^:private create-owned-fields
  #{:transaction-process/short-name
    :transaction-process/transaction-type
    :transaction-process/vehicle
    :transaction-process/target
    :transaction-process/template-kw
    :transaction-process/status})

;; ---------------------------------------------------------------------------
;; Value cleaning
;; ---------------------------------------------------------------------------

(defn- lcase [s] (some-> s str str/lower-case str/trim))

(def ^:private number-token
  "A number with its separators: digits joined by . , ' or spaces."
  #"-?\d(?:[\d.,'\s]*\d)?")

(def ^:private scales
  "Scale words an amount may carry after its number, each as a word (\"60m\",
   \"1.2 bn\", \"60 million\") and as a prefix of a currency code
   (\"60MEUR\"), which is case-sensitive so \"60 mins\" stays 60."
  [[#"(?i)^\s*(?:thousand|k)\b" #"^\s*[kK](?=[A-Z]{3}\b)" 1e3]
   [#"(?i)^\s*(?:million|mn|mm|m)\b" #"^\s*M(?=[A-Z]{3}\b)" 1e6]
   [#"(?i)^\s*(?:billion|bn|b)\b" #"^\s*(?:bn|B)(?=[A-Z]{3}\b)" 1e9]])

(defn- decimal-separator
  "Which of . and , separates the decimals of `digits` (spaces already turned
   into _), or nil for a whole number. Of both, the last one does; one used
   more than once groups thousands. A lone comma before exactly three digits
   groups them too (\"60,000\"), unless the number starts with 0 (\"0,125\"),
   and a lone dot always separates decimals, as in English."
  [digits]
  (let [dots   (count (re-seq #"\." digits))
        commas (count (re-seq #"," digits))]
    (cond
      (and (pos? dots) (pos? commas))
      (if (> (str/last-index-of digits ".") (str/last-index-of digits ",")) "." ",")

      (or (> dots 1) (> commas 1)) nil
      (= 1 dots)                   "."
      ;; Grouped thousands never start with 0, so "0,125" is a decimal.
      (= 1 commas)                 (when (or (str/includes? digits "_")
                                             (not (re-find #",\d{3}$" digits))
                                             (str/starts-with? digits "0"))
                                     ",")
      :else                        nil)))

(defn- read-number
  "The number a token spells, such as \"60,000,000\", \"1.250,50\",
   \"1 250 000\" or \"1,25\", or nil when its separators do not group
   thousands consistently."
  [token]
  (let [negative? (str/starts-with? token "-")
        digits    (str/replace (cond-> token negative? (subs 1)) #"[\s']" "_")
        decimal   (decimal-separator digits)
        parts     (if decimal
                    (str/split digits (if (= "." decimal) #"\." #","))
                    [digits])
        [whole fraction] parts
        groups    (str/split (str/replace whole #"[.,]" "_") #"_")]
    (when (and (<= (count parts) 2)
               (or (nil? fraction) (re-matches #"\d+" fraction))
               (if (next groups)
                 (and (re-matches #"[1-9]\d{0,2}" (first groups))
                      (every? #(re-matches #"\d{3}" %) (rest groups)))
                 (re-matches #"\d+" (first groups))))
      (cond-> (js/parseFloat (str (apply str groups) (when fraction (str "." fraction))))
        negative? -))))

(defn parse-number
  "The one number a loose value states, scaled by a word after it:
   \"GBP 60,000,000\" -> 6e7, \"EUR 1,25\" -> 1.25, \"11.8%\" -> 11.8,
   \"2.8x\" -> 2.8, \"1.16 (EUR per GBP)\" -> 1.16, \"GBP 60m\" -> 6e7.
   nil when it states none, or several (\"5 to 10\"), or its separators do
   not read one way: a partial number would be saved as a wrong one."
  [raw]
  (cond
    (number? raw) raw
    (string? raw) (let [text   (str/replace raw #"\([^)]*\)" " ")
                        tokens (re-seq number-token text)]
                    (when (= 1 (count tokens))
                      (let [token (first tokens)
                            after (subs text (+ (str/index-of text token) (count token)))
                            scale (or (some (fn [[word prefix factor]]
                                                      (when (or (re-find word after)
                                                                (re-find prefix after))
                                                        factor))
                                                    scales)
                                      1)]
                        (some-> (read-number token) (* scale)))))
    :else nil))

(defn- option-candidates
  "The strings an AI value might use to name an option whose stored value is `v`:
   its keyword name (\"no\"), its full keyword text (\"coi-answer/no\"), and the
   raw str (\":coi-answer/no\")."
  [v]
  (let [s (str v)]
    (cond-> #{(lcase s)}
      (keyword? v) (conj (lcase (name v)) (lcase (subs s 1))))))

(defn- resolve-enum
  "Resolve a loose AI value to one of `options` ({:value :label}), by the option
   value (any spelling) or its label. nil when nothing matches."
  [options raw]
  (let [target (lcase raw)]
    (some (fn [{:keys [value label]}]
            (when (or (contains? (option-candidates value) target)
                      (= target (lcase label)))
              value))
          options)))

(defn- iso-date->epoch-ms
  "A YYYY-MM-DD(...) string to UTC-midnight epoch-ms, matching the date picker's
   stored representation. nil when unparseable or not a real date: JavaScript
   would read 2026-02-30 as 2 March."
  [raw]
  (when (and (string? raw) (re-matches #"\d{4}-\d{2}-\d{2}.*" raw))
    (let [date (subs raw 0 10)
          t    (.getTime (js/Date. date))]
      (when (and (not (js/isNaN t))
                 (= date (subs (.toISOString (js/Date. t)) 0 10)))
        t))))

(defn- iso-datetime->epoch-ms
  "An ISO 8601 date-time string to epoch-ms, keeping its time and offset (one
   without an offset reads as UTC). A date alone is UTC midnight, as for a
   date. nil when unparseable."
  [raw]
  (when (string? raw)
    (if (re-matches #"\d{4}-\d{2}-\d{2}" raw)
      (iso-date->epoch-ms raw)
      (when (and (re-matches #"\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?" raw)
                 (iso-date->epoch-ms raw))
        (let [iso (cond-> (str/replace raw " " "T")
                    (not (re-find #"(Z|[+-]\d{2}:?\d{2})$" raw)) (str "Z"))
              t   (.getTime (js/Date. iso))]
          (when-not (js/isNaN t) t))))))

(defn clean-value
  "Clean an AI suggestion value to the stored type for `data-type` (using
   `options` for enums). Returns the typed value, or `drop-marker` when it can't
   be cleaned confidently."
  [data-type options raw]
  (case data-type
    (:data.type/text-simple :data.type/text-multiline :data.type/text-url)
    (if (string? raw) (str/trim raw) drop-marker)

    (:data.type/currency-value :data.type/number :data.type/multiplier)
    (or (parse-number raw) drop-marker)

    ;; Percentage fields store a fraction (0.118 renders as 11.8%), but the AI
    ;; reports human percentages ("11.8%" / 92), so scale down by 100.
    :data.type/percentage
    (if-let [n (parse-number raw)] (/ n 100) drop-marker)

    :data.type/integer
    (if-let [n (parse-number raw)] (long n) drop-marker)

    :data.type/keyword
    (or (resolve-enum options raw) drop-marker)

    ;; An ISO 4217 code, stored as its lower-case keyword (:eur).
    :data.type/currency-symbol
    (or (get currencies/value-lookup (some-> raw lcase)) drop-marker)

    :data.type/keyword-multi
    (let [items (if (coll? raw) raw [raw])
          vs    (vec (keep #(resolve-enum options %) items))]
      (if (seq vs) vs drop-marker))

    :data.type/boolean
    (cond
      (boolean? raw) raw
      (string? raw)  (case (lcase raw)
                       ("yes" "true") true
                       ("no" "false") false
                       drop-marker)
      :else drop-marker)

    :data.type/date
    (cond
      (number? raw) raw
      :else         (or (iso-date->epoch-ms raw) drop-marker))

    :data.type/datetime
    (cond
      (number? raw) raw
      :else         (or (iso-datetime->epoch-ms raw) drop-marker))

    ;; refs, year, unknown types: can't resolve a value safely from free text
    drop-marker))

;; ---------------------------------------------------------------------------
;; Builders (pure)
;; ---------------------------------------------------------------------------

(defn amount-of
  "The amount field whose currency `currency-field` holds:
   :company/revenue--sym -> :company/revenue."
  [currency-field]
  (keyword (namespace currency-field) (str/replace (name currency-field) #"--sym$" "")))

(defn- with-amounts-only
  "`cleaned` without the currencies of amounts it does not set: a currency
   alone would change what a stored amount means."
  [lookup cleaned]
  (into {}
        (remove (fn [[k _]]
                  (and (= :data.type/currency-symbol (:data-type (lookup k)))
                       (not (contains? cleaned (amount-of k))))))
        cleaned))

(defn- clean-fields
  "Clean a suggestion `{field-kw {:value _}}` map using `lookup` (field-kw ->
   {:data-type _ :options _}), dropping skip / unknown / uncleanable fields,
   and the currency of an amount that is dropped."
  [lookup fields {:keys [skip] :or {skip #{}}}]
  (->> fields
       (remove (fn [[k _]] (contains? skip k)))
       (keep (fn [[k {:keys [value]}]]
               (when-let [{:keys [data-type options]} (lookup k)]
                 (let [cleaned (clean-value data-type options value)]
                   (when-not (= drop-marker cleaned)
                     [k cleaned])))))
       (into {})
       (with-amounts-only lookup)))

(declare entity-schema-lookup)

(defn- dictionary-lookup
  "Field metadata lookup over a TP template dictionary ({field-kw entry}) of
   `entity-type`. An amount's currency field is not in the dictionary: it is
   looked up in the entity schema."
  [dictionary entity-type]
  (let [schema-lookup (entity-schema-lookup nil entity-type)]
    (fn [k]
      (if-let [e (get dictionary k)]
        {:data-type (:field/data-type e) :options (:field/options e)}
        (let [field (schema-lookup k)]
          (when (= :data.type/currency-symbol (:data-type field))
            field))))))

(defn- entity-schema-lookup
  "Field metadata lookup over a plain entity's registered schema, normalising
   option shapes to {:value :label}. `tenant-schema` adds custom fields (nil =
   static fields only)."
  [tenant-schema entity-type]
  (let [schema (schema-registry/get-schema tenant-schema entity-type)]
    (fn [k]
      (when-let [fs (get schema k)]
        {:data-type (:field/data-type fs)
         :options   (some->> (:options fs)
                             (mapv (fn [o] {:value (or (:value o) (:keyword o) (:key o))
                                            :label (:label o)})))}))))

(defn- calculated-fields
  "The calculated fields of `entity-type`: only the system writes them, so an
   update naming one is rejected."
  [entity-type]
  (->> (schema-registry/get-schema nil entity-type)
       (keep (fn [[k fs]] (when (:field/calculated-field fs) k)))
       set))

(defn root-field-updates
  "Cleaned {field-kw value} for the root transaction-process target, ready for a
   bulk entity update. Excludes the fields the create modal already set and the
   calculated ones."
  [result template]
  (let [fields (some-> result :suggestions :targets :transaction-process
                       first :fields)]
    (clean-fields (dictionary-lookup (:template/dictionary template)
                                     :entity.type/transaction-process)
                  fields {:skip (into create-owned-fields
                                      (calculated-fields :entity.type/transaction-process))})))

(defn entity-initial-values
  "Create-curtain `:initial-values` for a config-driven result: the root entity
   target's suggested fields, cleaned to stored types against the entity schema.
   Keyed by namespaced attribute, ready to seed the curtain's entity-edits so the
   normal create persists them (simple entities need no post-create apply).
   Pass `tenant-schema` when the suggestions may name custom fields."
  ([result]
   (entity-initial-values result nil))
  ([result tenant-schema]
   (let [entity-type (-> result :request-context :entity-type)
         slug        (keyword (name entity-type))
         fields      (some-> result :suggestions :targets (get slug) first :fields)]
     (clean-fields (entity-schema-lookup tenant-schema entity-type) fields {}))))

(defn creator-initial-values
  "Create-curtain `:initial-values` for a config-driven result: what the user
   had entered when they dropped the document (the frozen draft), overlaid with
   the cleaned suggestions."
  [result tenant-schema]
  (merge (-> result :request-context :draft)
         (entity-initial-values result tenant-schema)))

(defn- slot-index
  "slug (name of the slot ref) -> {:slot-attr :entity-type :template}."
  [template]
  (into {}
        (map (fn [[slot-kw entry]]
               [(name slot-kw) {:slot-attr   slot-kw
                                :entity-type (:slot/entity-type entry)
                                :template    (:slot/template entry)}]))
        (resolver/entity-slots (:template/dictionary template))))

(defn declaration-creates
  "One {:slot-attr :fields} per suggested declaration slot entity that has at
   least one cleanable field. Only :entity.type/declaration slots are applied;
   decision / attestation slots are internal review-stage artefacts and are left
   for the user (the AI returns them empty for preparation documents)."
  [result template]
  (let [idx (slot-index template)]
    (for [[slug suggestions] (some-> result :suggestions :targets)
          :let  [info (get idx (name slug))]
          :when (and info (= :entity.type/declaration (:entity-type info)))
          suggestion suggestions
          :let  [fields (clean-fields (dictionary-lookup (:template info) (:entity-type info))
                                      (:fields suggestion)
                                      {:skip (calculated-fields (:entity-type info))})]
          :when (seq fields)]
      {:slot-attr (:slot-attr info)
       :fields    fields})))

;; ---------------------------------------------------------------------------
;; Effecting
;; ---------------------------------------------------------------------------

(defn- tracker
  "A callback for `n` writes, each calling it once with whether it succeeded.
   After the last, calls `on-done` with whether all of them did."
  [n on-done]
  (let [left (atom n)
        ok?  (atom true)]
    (when (zero? n)
      (on-done true))
    (fn [success?]
      (when-not success?
        (reset! ok? false))
      (when (zero? (swap! left dec))
        (on-done @ok?)))))

(defn- create-declarations!
  [process-id declarations on-done]
  (let [done! (tracker (count declarations) on-done)]
    (doseq [{:keys [slot-attr fields]} declarations]
      (let [temp-id (fu/create-temp-id "temp-declaration-")
            entity  (merge {:db/id              temp-id
                            :entity/type        :entity.type/declaration
                            :declaration/draft? false}
                           fields)]
        (declaration-actions/*->create-with-response
         {:declaration-id temp-id
          :process-id     process-id
          :slot-attr      slot-attr
          :entities       {temp-id entity}}
         (fn [_] (done! true))
         (fn [err]
           (logging/error "enrich: failed to create declaration slot" err)
           (done! false)))))))

(defn currency-field?
  "Whether `field` holds the currency of an amount (see amount-of)."
  [field]
  (str/ends-with? (name field) "--sym"))

(defn write-groups
  "`root` split into the updates the per-field fallback sends: each field on
   its own, but an amount together with its currency, so that neither lands
   without the other."
  [root]
  (->> root
       (remove (fn [[field _]] (currency-field? field)))
       (map (fn [[field value]]
              (let [currency (keyword (namespace field) (str (name field) "--sym"))]
                (cond-> {field value}
                  (contains? root currency) (assoc currency (get root currency))))))))

(defn- apply-root-per-field!
  "Fallback when the one-shot bulk update is rejected (it is atomic per entity, so
   a single bad attribute loses them all): push each field on its own, an amount
   with its currency, so the good ones still land."
  [process-id root on-done]
  (let [groups (write-groups root)
        done!  (tracker (count groups) on-done)]
    (doseq [group groups]
      (entity-actions/*->entities-update!
       [{:db/id process-id :data group}]
       (fn [_] (done! true))
       (fn [err]
         (logging/error (str "enrich: fields " (keys group) " could not be prefilled") err)
         (done! false))))))

(defn apply-result!
  "Apply the cleaned root fields and declaration slots onto `process-id`, then
   navigate to the created process. Calls `on-done` with whether every write
   succeeded; the ones that fail are logged and the others still land."
  [{:keys [process-id root declarations on-done]}]
  (let [navigate!     #(routing/open-entity-type :entity.type/transaction-process process-id)
        declarations! (fn [root-ok?]
                        (create-declarations! process-id declarations
                                              #(on-done (and root-ok? %))))]
    (if (seq root)
      (entity-actions/*->entities-update!
       [{:db/id process-id :data root}]
       (fn [_] (navigate!) (declarations! true))
       (fn [err]
         (logging/error "enrich: bulk prefill rejected, retrying per field" err)
         (navigate!)
         (apply-root-per-field! process-id root declarations!)))
      (do (navigate!)
          (declarations! true)))))

(rf/reg-fx ::run apply-result!)

(rf/reg-fx
 ::tell-not-applied
 (fn [_]
   (toast/error (loc/tr :enrich-process/apply-failed))))

(defn not-applied
  "Applying the result failed: say so. The job stays listed with its target,
   offering to apply again."
  [_ [_ result-id]]
  {::tell-not-applied true
   :fx [[:dispatch [::enrich-event/apply-failed result-id]]]})

(defn create-target!
  "Create the entity `data` for the job `result-id`: the backend records it as
   the job's target in the same transaction, and returns the entity the job
   already has instead of creating another. Takes the same callbacks as
   entity-actions/*->create."
  [result-id data on-success! on-error!]
  (-> (conn/sendp-rpc! :ai-enrichment/create-target
                       {:result-id result-id :entity data}
                       120000)
      (p/then (fn [{:keys [body] :as resp}]
                (if (:db/id body)
                  (on-success! resp)
                  (on-error! resp))))
      (p/catch on-error!)))

(defn missing-fields
  "The fields of `root` that `process` still lacks: a value set since, by the
   user or an earlier attempt, is never replaced. A currency goes with its
   amount, whatever the process holds for it (a default, say)."
  [root process]
  (let [missing? (fn [field] (cu/blank-value? (get process field)))]
    (into {}
          (filter (fn [[field _]]
                    (missing? (cond-> field (currency-field? field) amount-of))))
          root)))

(defn resume-created
  "Event: the job's process exists (the create recorded it as the job's
   target), loaded as `process`, also after a failed apply. Build the apply
   payload from the carried `result` (the enrich state may already have been
   cleared by the modal's on-close) and run it, which also navigates to the
   process. Only the fields the process still lacks are written, and the
   declaration slots it already holds are not created again: another tab, or
   an earlier attempt, may have filled them. The job is resolved once every
   write succeeded; otherwise it stays listed, offering to apply again."
  [{:keys [db]} [_ process-id result process]]
  (let [template  (registry/get-template (-> result :request-context :template-kw))
        result-id (:result-id result)]
    {:db  (enrich-model/set-target db result-id process-id)
     :fx  [[::run {:process-id   process-id
                   :root         (missing-fields (root-field-updates result template) process)
                   :declarations (remove #(some? (get process (:slot-attr %)))
                                         (declaration-creates result template))
                   :on-done      (fn [ok?]
                                   (rf/dispatch (if ok?
                                                  [::enrich-event/resolve result-id]
                                                  [::not-applied result-id])))}]]}))

(rf/reg-event-fx ::resume-created resume-created)

(defn created
  "Event: the review's create returned the job's process, new or, when another
   tab created it first, the existing one. Load it before applying, so what it
   already holds is kept."
  [_ [_ process-id result]]
  {::load-target {:job (assoc result :target-id process-id)}})
(rf/reg-event-fx ::not-applied not-applied)

(defn retry
  "Apply a job's result again onto its target, the process it created. The
   suggestions are fetched first: a reload drops them. While an apply of the
   job runs, another is not started: both would see the same empty slots."
  [{:keys [db]} [_ result-id]]
  (if (:applying? (enrich-model/job db result-id))
    {}
    {:db         (enrich-model/start-applying db result-id)
     ::ajax/send {:operation :ai-enrichment/result
                  :body      {:result-id result-id}
                  :on-ok     [::retry-fetched]
                  :on-error  [::enrich-event/apply-failed result-id]}}))

(rf/reg-fx
 ::load-target
 (fn [{:keys [job]}]
   (entity-scope/load-entity!
    {:entity-id (:target-id job)
     :on-loaded (fn [process _]
                  (rf/dispatch [::resume-created (:target-id job) job process]))
     :on-error  (fn [error]
                  (logging/error "enrich: could not load the process to apply onto" error)
                  (rf/dispatch [::enrich-event/apply-failed (:result-id job)]))})))

(rf/reg-event-fx ::created created)

(defn retry-fetched
  "The job is fetched: load its process, to see which slots the earlier attempt
   already filled, then apply again."
  [{:keys [db]} [_ resp]]
  (let [job (:body resp)]
    {:db           (enrich-model/job-loaded db job)
     ::load-target {:job job}}))

(rf/reg-event-fx ::retry retry)
(rf/reg-event-fx ::retry-fetched retry-fetched)
